import { useCallback, useEffect, useRef, useState } from 'react';

export interface InfinitePage<T> {
  items: T[];
  totalCount: number;
}

export interface UseInfinitePaginationOptions {
  /** Page size handed to the fetcher. */
  per: number;
  /** Distance (px) ahead of the sentinel at which the next page kicks in. */
  rootMargin?: string;
  /** Custom scroll container for the IntersectionObserver. Null/undefined →
   *  viewport (the page itself scrolls). Pass an element ref's `.current`
   *  when the list lives inside its own overflow container (e.g. a modal). */
  scrollRoot?: Element | null;
}

export interface UseInfinitePaginationResult<T> {
  items: T[];
  /** Backend-reported total across all pages — drives stats and "X of Y"
   *  copy. Falls back to `items.length` while the first page is in flight. */
  totalCount: number;
  /** Page size passed into the hook — exposed so callers can size the
   *  loading-more skeleton row to match the next page's expected length. */
  per: number;
  /** First-page load — raised by the bootstrap AND by every `refetch`. */
  loading: boolean;
  /**
   * True only while the very first page-1 load is in flight, i.e. this list
   * has never had anything to show. **Gate skeletons on this, not on
   * `loading`.**
   *
   * `loading` goes up on every `refetch` too, and stale-while-revalidate only
   * hides that for a list that already has items: on a genuinely empty feed
   * `items` stays `[]`, so a `loading && items.length === 0` gate re-fires on
   * every tab switch, visibilitychange and poll tick — a placeholder flashing
   * for a split second with nothing behind it.
   */
  initialLoading: boolean;
  /** Subsequent-page load triggered by the sentinel. */
  loadingMore: boolean;
  /** True once the backend reports no more pages (empty page or
   *  totalCount reached). */
  reachedEnd: boolean;
  error: string | null;
  /** Refetch from page 1. Stale-while-revalidate: the accumulated list
   *  stays on screen while the fresh first page is in flight and is
   *  replaced when it lands — so callers gating their skeleton on
   *  `loading && items.length === 0` only skeleton when there's genuinely
   *  nothing to show yet. */
  refetch: () => Promise<void>;
  /** Trigger the next page manually (e.g. a "Load more" button). */
  loadMore: () => void;
  /** Attach to a sentinel element at the end of the list; visibility
   *  triggers `loadMore`. */
  sentinelRef: (node: Element | null) => void;
}

/**
 * Generic infinite-pagination hook. The caller owns the fetcher (so each
 * endpoint maps its params/shape itself); the hook owns the cursor, the
 * accumulated list, the `reachedEnd` decision and the IntersectionObserver
 * sentinel. Same paging mechanics that lived in `useJustPulledPaginated`,
 * generalised so every paginated list in the app can share it.
 *
 * The fetcher MUST be stable (memoise with `useCallback` upstream) or every
 * render re-creates the bootstrap effect — `useCallback(..., [client, ...])`
 * upstream is the right idiom.
 */
export function useInfinitePagination<T>(
  fetcher: (page: number, per: number) => Promise<InfinitePage<T>>,
  options: UseInfinitePaginationOptions,
): UseInfinitePaginationResult<T> {
  const { per, rootMargin = '400px', scrollRoot = null } = options;

  const [items, setItems] = useState<T[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Flipped once the first page-1 attempt finishes — success or failure alike,
  // since either way the caller now has something to render (a list, an empty
  // state or an error) and must not fall back to a skeleton.
  const [settledOnce, setSettledOnce] = useState(false);

  const pageRef = useRef(1);
  const inFlightRef = useRef(false);
  const cancelledRef = useRef(false);
  const sentinelNodeRef = useRef<Element | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const fetchPage = useCallback(
    async (page: number, isInitial: boolean) => {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      if (!isInitial) setLoadingMore(true);
      try {
        const res = await fetcher(page, per);
        if (cancelledRef.current) return;
        setTotalCount(res.totalCount);
        setItems((prev) => {
          const next = isInitial ? res.items : [...prev, ...res.items];
          if (res.totalCount > 0 && next.length >= res.totalCount) {
            setReachedEnd(true);
          } else if (res.items.length === 0) {
            setReachedEnd(true);
          }
          return next;
        });
        pageRef.current = page + 1;
        if (isInitial) setError(null);
      } catch (e) {
        if (cancelledRef.current) return;
        if (isInitial) {
          setError(e instanceof Error ? e.message : 'Failed to load');
        }
        setReachedEnd(true);
      } finally {
        inFlightRef.current = false;
        if (!cancelledRef.current) {
          if (isInitial) {
            setLoading(false);
            setSettledOnce(true);
          }
          setLoadingMore(false);
        }
      }
    },
    [fetcher, per],
  );

  // Deliberately does NOT clear `items`/`totalCount`: the stale list keeps
  // rendering during the reload (no skeleton flash on tab revisits) and is
  // atomically replaced by the fresh page-1 response in `fetchPage`.
  const refetch = useCallback(async () => {
    cancelledRef.current = false;
    pageRef.current = 1;
    setLoading(true);
    setLoadingMore(false);
    setReachedEnd(false);
    setError(null);
    await fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    cancelledRef.current = false;
    pageRef.current = 1;
    setItems([]);
    setTotalCount(0);
    setLoading(true);
    setLoadingMore(false);
    setReachedEnd(false);
    setError(null);
    // Reset here but NOT in `refetch`: a new `fetcher` identity means a new
    // data source with nothing rendered for it yet, so a skeleton is right
    // again. A refetch reloads the same source and must stay silent.
    setSettledOnce(false);
    void fetchPage(1, true);
    return () => {
      cancelledRef.current = true;
    };
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || reachedEnd) return;
    void fetchPage(pageRef.current, false);
  }, [loading, loadingMore, reachedEnd, fetchPage]);

  // IntersectionObserver wired to whatever node the caller pins via the
  // returned `sentinelRef` callback. Re-wires when either the node OR the
  // `loadMore` identity changes (so the observer always points at the
  // freshest closure).
  const sentinelRef = useCallback(
    (node: Element | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      sentinelNodeRef.current = node;
      if (!node) return;
      const io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) loadMore();
        },
        { root: scrollRoot, rootMargin },
      );
      io.observe(node);
      observerRef.current = io;
    },
    [loadMore, rootMargin, scrollRoot],
  );

  return {
    items,
    totalCount,
    per,
    loading,
    initialLoading: loading && !settledOnce,
    loadingMore,
    reachedEnd,
    error,
    refetch,
    loadMore,
    sentinelRef,
  };
}
